// Traffic Fines: the words and marks on the screen. Every fact behind them (the
// order of the queue, days since the offence, the AARTO flag, who is proposed and
// why) comes from the fines_* functions in db/migrations/0340 to 0343. These
// functions only say it. CLAUDE.md rule 4.

export type FineStatus = 'new' | 'attributed' | 'nominated' | 'paid' | 'disputed' | 'written_off'
export type FineMethod = 'sheet_named' | 'roster_exact' | 'roster_ambiguous' | 'no_shift' | 'undatable'
export type FineSource = 'manual' | 'legacy_sheet' | 'owner_statement_backfill'

export type Proposed = { driver_id: string | null; name: string | null; method: FineMethod; confidence: 'high' | 'medium' | 'low' | null }

export type QueueRow = {
  id: string
  source: FineSource
  is_history: boolean
  notice_number: string | null
  issuing_authority: string | null
  vehicle_id: string | null
  registration: string | null
  owner_id: string | null
  owner_name: string | null
  owner_basis: string | null
  offence_at: string | null
  sheet_date: string | null
  statement_period: string | null
  amount_zar: number | null
  status: FineStatus
  days_since_offence: number | null
  aarto_flag: 'red' | 'overdue' | null
  driver_id: string | null
  driver_name: string | null
  confirmed_at: string | null
  proposed: Proposed[]
  top_method: FineMethod | null
  charged_to: 'driver' | 'owner' | 'fleetly' | null
  has_evidence: boolean
  paid_by_payment: boolean
  reversed: boolean
  created_at: string
  total_count: number
}

export type QueueResult = { rows: QueueRow[]; total: number; as_at: string; resolved?: boolean }

export type Candidate = {
  id: string
  driver_id: string | null
  name: string | null
  method: FineMethod
  confidence: 'high' | 'medium' | 'low' | null
  basis: string
  shift_id: string | null
  proposed_at: string
  is_you: boolean
}

export type FineEvent = { kind: string; at: string; actor: string | null; detail: Record<string, unknown> }

export type FineDetail = {
  fine: {
    id: string
    source: FineSource
    source_ref: string | null
    entry_kind: 'notice' | 'reversal'
    vehicle_id: string | null
    registration: string | null
    notice_number: string | null
    issuing_authority: string | null
    offence_at: string | null
    location: string | null
    offence_description: string | null
    amount_zar: number
    paid_amount_zar: number | null
    paid_at: string | null
    discount_deadline: string | null
    sheet_date: string | null
    statement_period: string | null
    status: FineStatus
    owner_id: string | null
    owner_name: string | null
    owner_basis: string | null
    driver_id: string | null
    driver_name: string | null
    confirmed_at: string | null
    confirmed_by_name: string | null
    confirmation_note: string | null
    charged_to: 'driver' | 'owner' | 'fleetly' | null
    charged_to_decided_at: string | null
    charged_to_decided_by_name: string | null
    evidence_path: string | null
    extracted_fields: Record<string, unknown> | null
    notes: string | null
    created_at: string
    created_by_name: string | null
    days_since_offence: number | null
    // 0390: what the notice was for, and the two speeds on a speeding notice.
    offence_category: 'speeding' | 'red_light' | 'parking' | 'licence_disc' | 'other' | null
    offence_category_basis: 'description_rule' | 'manual' | null
    recorded_kmh: number | null
    limit_kmh: number | null
    over_limit_kmh: number | null
  }
  candidates: Candidate[]
  reversals: { id: string; amount_zar: number; sheet_date: string | null; statement_period: string | null; notes: string | null }[]
  payment: { id: string; amount_zar: number; statement_period: string | null; notes: string | null } | null
  statement_line: { id: string; amount_zar: number; period: string; label: string; issued: boolean } | null
  possible_duplicates: { id: string; notice_number: string | null; offence_at: string; amount_zar: number }[]
  events: FineEvent[]
  can_delete: boolean
  can_decide_charged_to: boolean
  can_edit: boolean
  conflict: string | null
  // 0483: where a speeding fine stands on the driving conduct ladder. Null for
  // anything that is not a speeding notice.
  conduct: {
    bucket: string
    attribution_status: string | null
    unattributed_reason: string | null
    reckless: boolean | null
    stage: string | null
    outcome: string | null
    response_due_on: string | null
    coaching_only_reason: string | null
    case_id: string | null
    consent: { status: string; offered_on: string; decided_on: string | null; amount_zar: number } | null
  } | null
}

export type Options = {
  vehicles: { id: string; registration: string; label: string; owner_name: string | null; status: string }[]
  drivers: { id: string; name: string; status: string }[]
  owners: { id: string; name: string }[]
  is_admin: boolean
  counts: Partial<Record<FineStatus, number>> | null
}

export type Reading = {
  method: 'vision' | 'none' | 'failed'
  model: string | null
  fields: {
    registration: string | null
    notice_number: string | null
    issuing_authority: string | null
    offence_date: string | null
    offence_time: string | null
    location: string | null
    offence_description: string | null
    amount_zar: number | null
    discount_amount_zar: number | null
    discount_deadline: string | null
  }
  warnings: string[]
}

export const STATUSES: FineStatus[] = ['new', 'attributed', 'nominated', 'paid', 'disputed', 'written_off']

const STATUS_LABEL: Record<FineStatus, string> = {
  new: 'New',
  attributed: 'Driver confirmed',
  nominated: 'Nominated',
  paid: 'Paid',
  disputed: 'Disputed',
  written_off: 'Written off',
}

export function statusLabel(s: string): string {
  return STATUS_LABEL[s as FineStatus] ?? s
}

export function statusTone(s: string): 'green' | 'red' | 'amber' | 'neutral' | 'brand' {
  if (s === 'paid' || s === 'written_off') return 'green'
  if (s === 'disputed') return 'amber'
  if (s === 'nominated' || s === 'attributed') return 'brand'
  return 'neutral'
}

const METHOD_LABEL: Record<FineMethod, string> = {
  sheet_named: 'Named on the old fines sheet',
  roster_exact: 'On the roster at the time',
  roster_ambiguous: 'Near a handover, more than one driver possible',
  no_shift: 'Nobody from Fleetly was rostered',
  undatable: 'Month only, every driver that month',
}

export function methodLabel(m: string | null): string {
  if (!m) return 'No proposal'
  return METHOD_LABEL[m as FineMethod] ?? m
}

export function sourceLabel(s: string): string {
  if (s === 'legacy_sheet') return 'History: old fines sheet'
  if (s === 'owner_statement_backfill') return 'History: owner statement'
  return 'Captured'
}

export function chargedToLabel(c: string | null): string {
  if (c === 'driver') return 'The driver carries it'
  if (c === 'owner') return 'The owner carries it'
  if (c === 'fleetly') return 'Fleetly carries it'
  return 'Not decided'
}

export function ownerBasisLabel(b: string | null): string {
  if (b === 'statement_month') return 'Owner on the statement for that month'
  if (b === 'current_owner') return 'Current owner, no statement for that month yet'
  if (b === 'current_owner_transfer_after') return 'Current owner, but the vehicle changed owner after the offence. Check who owned it.'
  return 'Owner not known'
}

// What a missing value reads as. Words, not a dash: a blank that reads as a
// glyph is easy to mistake for zero, and a missing figure shows as missing.
export const NOT_RECORDED = 'Not recorded'

const SAST = 'Africa/Johannesburg'

export function formatSastDateTime(iso: string | null | undefined): string {
  if (!iso) return NOT_RECORDED
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return NOT_RECORDED
  return d.toLocaleString('en-ZA', { timeZone: SAST, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
}

/** A calendar date (YYYY-MM-DD) as a date, never shifted by the viewer's zone. */
export function formatDay(day: string | null | undefined): string {
  if (!day) return NOT_RECORDED
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day)
  if (!m) return NOT_RECORDED
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.toLocaleDateString('en-ZA', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' })
}

export function formatMonth(day: string | null | undefined): string {
  if (!day) return NOT_RECORDED
  const m = /^(\d{4})-(\d{2})/.exec(day)
  if (!m) return NOT_RECORDED
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1))
  return d.toLocaleDateString('en-ZA', { timeZone: 'UTC', month: 'long', year: 'numeric' })
}

/** What the row can honestly say about when: the offence, or failing that, what the source had. */
export function whenLabel(r: { offence_at: string | null; sheet_date: string | null; statement_period: string | null }): string {
  if (r.offence_at) return formatSastDateTime(r.offence_at)
  if (r.sheet_date) return `Sheet date ${formatDay(r.sheet_date)}`
  if (r.statement_period) return `Statement for ${formatMonth(r.statement_period)}`
  return 'Offence date not known'
}

/** The AARTO clock in words, or null when there is no clock to show. */
export function aartoLabel(r: { aarto_flag: string | null; days_since_offence: number | null }): string | null {
  if (r.days_since_offence === null || r.aarto_flag === null) return null
  const d = r.days_since_offence
  if (r.aarto_flag === 'overdue') return `${d} days since the offence, past the 32 day nomination window`
  return `${d} days since the offence, nominate by day 32`
}

// The same rand the owner statements print (utils/statements.ts): R1,250.00.
// en-ZA would print R1 250,00, and a fine should read the way the statement it
// lands on reads.
const ZAR = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function formatZar(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return NOT_RECORDED
  const body = `R${ZAR.format(Math.abs(v)).replace(/ /g, ' ')}`
  return v < 0 ? `(${body})` : body
}

/**
 * The offence moment the manager typed, as an ISO timestamp in Johannesburg
 * time. South Africa keeps no daylight saving, so +02:00 is the zone all year.
 * Null when either part is missing or malformed, so the save refuses rather
 * than guessing midnight.
 */
export function toOffenceIso(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null
  const [h, m] = time.split(':').map(Number)
  if (h > 23 || m > 59) return null
  return `${date}T${time}:00+02:00`
}

/** Normalised for comparison: a registration as printed on a notice and as held for a vehicle. */
export function samePlate(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  return a.replace(/[\s-]/g, '').toUpperCase() === b.replace(/[\s-]/g, '').toUpperCase()
}

export function firstProposal(r: { proposed: Proposed[] }): Proposed | null {
  return r.proposed.find((p) => p.driver_id) ?? r.proposed[0] ?? null
}

export function eventLabel(e: FineEvent): string {
  const d = e.detail ?? {}
  switch (e.kind) {
    case 'captured': return 'Captured'
    case 'loaded_from_history': return 'Loaded from history'
    case 'edited': return 'Details corrected'
    case 'confirmed_driver': return `Driver confirmed: ${String(d.driver ?? 'unknown')}${d.previous ? `, was ${String(d.previous)}` : ''}`
    case 'confirmed_no_driver': return 'Confirmed that no Fleetly driver was driving'
    case 'status': return `Status: ${statusLabel(String(d.from ?? ''))} to ${statusLabel(String(d.to ?? ''))}`
    case 'charged_to': return `Who carries it: ${chargedToLabel((d.to as string) ?? null)}`
    case 'deleted': return 'Deleted'
    default: return e.kind
  }
}
